import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  CardActionArea,
  Paper,
} from '@mui/material';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import ApiContext from 'contexts/apiContext';
import EmptyState from 'common/EmptyState';
import ErrorContext from 'contexts/errorContext';
import { Link } from 'react-router-dom';
import { Permission } from 'consts';
import PackCard from 'components/PackCard';
import PackDialog from 'components/PackDialog';
import { Pagination } from '@mui/material';
import PermissionTooltip from 'common/PermissionTooltip';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import RefreshIndicator from 'common/RefreshIndicator';
import Spinner from 'common/Spinner';
import StoreContext from 'contexts/storeContext';
import Toast from 'common/Toast';
import debounce from 'lodash.debounce';
import { makeStyles } from 'tss-react/mui';
import { Pack, PackBeingCreated } from 'types/pack';
import { AutoAwesome, Diversity3, ExpandMore, ShoppingBag } from '@mui/icons-material';
import { useMountLogs, useSurprisePackHandlingEnabled } from 'hooks';
import { DashboardTip } from 'components/Dashboard/DashboardTip';
import ChallengeProgress from 'components/ChallengeProgress';
import { ChallengeProgress as ChallengeProgressType } from 'types/challenge';
import { SURPRISE_NAME_PREFIX_LABEL } from 'surprisePack';

const useStyles = makeStyles()((theme) => ({
  box: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
  },
  topItem: {
    marginRight: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  newPackButton: {
    whiteSpace: 'nowrap',
    minWidth: 'max-content',
  },
  packTypeCard: {
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.primary.light}`,
    backgroundColor: theme.palette.grey[50],
    transition: 'all 0.2s ease',
    height: '100%',
    overflow: 'hidden',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      boxShadow: theme.shadows[3],
    },
  },
  packTypeCardContent: {
    padding: theme.spacing(2),
    minHeight: 140,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    backgroundColor: 'transparent',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.common.white,
    },
  },
  packTypeDialogTitle: {
    fontWeight: 700,
    color: theme.palette.primary.dark,
    paddingBottom: theme.spacing(0.5),
  },
  packTypeDialogSubtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    fontWeight: 500,
  },
  packTypeIcon: {
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.dark,
  },
  packTypeTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.25),
  },
  surpriseIcon: {
    color: theme.palette.primary.dark,
  },
  searchBar: {
    flex: 1,
    minWidth: 200,
    maxWidth: 500,
  },
  paginationContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: theme.spacing(2),
  },
  accordion: {
    marginBottom: theme.spacing(3),
    boxShadow: theme.shadows[2],
    borderRadius: theme.spacing(1),
    '&:before': {
      display: 'none',
    },
  },
  accordionSummary: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    minHeight: '48px',
    borderRadius: theme.spacing(1),
    '&.Mui-expanded': {
      minHeight: '48px',
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
    '& .MuiAccordionSummary-content': {
      margin: '8px 0',
      '&.Mui-expanded': {
        margin: '8px 0',
      },
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      color: theme.palette.primary.contrastText,
    },
  },
  accordionDetails: {
    padding: 0,
  },
}));

export default function PacksScreen() {
  const { classes } = useStyles();
  const { api, currentUser } = useContext(ApiContext);
  const { store, hasPermission } = useContext(StoreContext);
  const { setError } = useContext(ErrorContext);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [typeDialogIsOpen, setTypeDialogIsOpen] = useState(false);
  const [createDialogIsOpen, setCreateDialogIsOpen] = useState(false);
  const [createIsSurprise, setCreateIsSurprise] = useState(false);
  const [packs, setPacks] = useState<Pack[]>([]);
  const [search, setSearch] = useState<string>();
  const [activeOnly, setActiveOnly] = useState(false);
  const [autoPublishedOnly, setAutoPublishedOnly] = useState(false);
  const mobile = useMediaQuery('(max-width: 600px)');
  const [packIdBeingEdited, setPackIdBeingEdited] = useState<number>();
  const [challengeProgress, setChallengeProgress] = useState<ChallengeProgressType | null>(null);

  useMountLogs('PacksScreen');

  // pagination
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(0);

  const hasEditPermission = hasPermission(Permission.EDIT_PACK);
  const surprisePackHandlingEnabled = useSurprisePackHandlingEnabled(store, currentUser);

  const refreshChallengeProgress = useCallback(() => {
    if (store?.id) {
      api.stores
        .getChallengeProgress(store.id)
        .then((res) => {
          setChallengeProgress(res.data);
        })
        .catch((error) => {
          // Silently fail if challenge progress is not available
          console.error('Error fetching challenge progress:', error);
        });
    }
  }, [store.id, api]);

  const debouncedRefreshChallengeProgress = useMemo(
    () => debounce(() => refreshChallengeProgress(), 500),
    [refreshChallengeProgress]
  );

  useEffect(() => {
    return () => {
      debouncedRefreshChallengeProgress.cancel();
    };
  }, [debouncedRefreshChallengeProgress]);

  const refreshPacks = useCallback(
    (search?: string, page?: number) => {
      if (store) {
        setRefreshing(true);
        let params: any = { search, page };
        if (activeOnly) {
          params['quantity__gt'] = 0;
        }
        if (autoPublishedOnly) {
          params['auto_publish_qty__gt'] = 0;
        }
        api.packs
          .list(store.id, params)
          .then((res) => {
            setPacks(res.data.results);
            setNumPages(res.data.num_pages);
            // Refresh challenge progress as a side effect with 500ms delay
            debouncedRefreshChallengeProgress();
          })
          .catch(setError)
          .finally(() => {
            setLoading(false);
            setRefreshing(false);
            setPackIdBeingEdited(undefined);
          });
      }
    },
    [store, api, setError, activeOnly, autoPublishedOnly, debouncedRefreshChallengeProgress],
  );

  const debouncedRefreshPacks = useMemo(() => debounce((s) => refreshPacks(s), 500), [refreshPacks]);

  const refreshPacksSameParams = useCallback(() => refreshPacks(search, page), [refreshPacks, search, page]);

  useEffect(() => debouncedRefreshPacks(search), [debouncedRefreshPacks, search]);

  useEffect(() => {
    refreshChallengeProgress();
  }, [refreshChallengeProgress]);

  const closeCreateDialog = (success: boolean) => {
    setCreateDialogIsOpen(false);
    if (success) {
      refreshPacks();
    }
  };

  const openTypeDialog = () => {
    if (!surprisePackHandlingEnabled) {
      setCreateIsSurprise(false);
      setCreateDialogIsOpen(true);
      return;
    }
    setTypeDialogIsOpen(true);
  };

  const closeTypeDialog = () => {
    setTypeDialogIsOpen(false);
  };

  const selectPackType = (isSurprise: boolean) => {
    setCreateIsSurprise(isSurprise);
    setTypeDialogIsOpen(false);
    setCreateDialogIsOpen(true);
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handlePagination = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    refreshPacks(search, value);
  };

  const handleActiveOnly = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(1);
    setActiveOnly(event.target.checked);
  };

  const handleAutoPublishedOnly = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(1);
    setAutoPublishedOnly(event.target.checked);
  };

  return (
    <>
      <Box className={classes.box}>
        <PermissionTooltip hasPermission={hasEditPermission}>
          <Button
            className={`${classes.newPackButton} ${classes.topItem}`}
            variant='contained'
            color='primary'
            onClick={openTypeDialog}
            disabled={!hasEditPermission}
            startIcon={<AddIcon />}
          >
            Pack
          </Button>
        </PermissionTooltip>
        <Link to='images'>
          <Button
            className={`${classes.newPackButton} ${classes.topItem}`}
            variant='contained'
            color='primary'
            startIcon={<PhotoLibraryIcon />}
          >
            Imágenes
          </Button>
        </Link>
        {!!store.organization && (
          <Link to='shared'>
            <Button
              className={`${classes.newPackButton} ${classes.topItem}`}
              variant='contained'
              color='primary'
              startIcon={<Diversity3 />}
            >
              Packs compartidos
            </Button>
          </Link>
        )}
        <FormControlLabel
          className={classes.topItem}
          control={<Checkbox checked={activeOnly} onChange={handleActiveOnly} color='primary' />}
          label={
            <Typography variant='body2' noWrap sx={{ fontWeight: 600 }}>
              Sólo activos
            </Typography>
          }
        />
        <FormControlLabel
          className={classes.topItem}
          control={<Checkbox checked={autoPublishedOnly} onChange={handleAutoPublishedOnly} color='primary' />}
          label={
            <Typography variant='body2' noWrap sx={{ fontWeight: 600 }}>
              Sólo con publicación automática
            </Typography>
          }
        />
        <TextField
          label='Buscar'
          value={search}
          variant='filled'
          type='search'
          onChange={onSearchChange}
          className={`${classes.searchBar} ${classes.topItem}`}
          size='small'
        />
      </Box>
      {!mobile && (
        <>
          <Box sx={{ display: 'flex', mb: 2 }}>
            <DashboardTip sx={{ m: 0, border: 'none', maxWidth: 700, mt: -1 }} textSx={{ opacity: 1 }}>
              ¡Psst! <b>Tener packs con publicación automática no sólo te facilita la operativa</b>, si no que asegura que tu tienda es
              visible en la app durante todo el día, atrayendo más usuarios y pedidos.
            </DashboardTip>
            <DashboardTip sx={{ m: 0, border: 'none', maxWidth: 700, mt: -1, pl: 0 }} textSx={{ opacity: 1 }}>
              ¡A mayor información, más ventas! <b>Usa imágenes, nombres y descripciones representativas y de calidad</b> en tus packs para
              que los usuarios elijan confiar en tu tienda y tus productos.
            </DashboardTip>
          </Box>
          {challengeProgress && (
            <Accordion className={classes.accordion}>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="challenge-progress-content"
                id="challenge-progress-header"
                className={classes.accordionSummary}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  🏆 Desafíos Semanales
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <ChallengeProgress challengeProgress={challengeProgress} packs={packs} />
              </AccordionDetails>
            </Accordion>
          )}
        </>
      )}

      <Dialog open={typeDialogIsOpen} onClose={closeTypeDialog} fullWidth maxWidth='sm'>
        <DialogTitle className={classes.packTypeDialogTitle}>Crear pack</DialogTitle>
        <DialogContent>
          <Typography variant='body2' className={classes.packTypeDialogSubtitle}>
            Elige el tipo de pack que quieres crear.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Paper className={classes.packTypeCard} elevation={0}>
                <CardActionArea onClick={() => selectPackType(false)} className={classes.packTypeCardContent}>
                  <ShoppingBag className={classes.packTypeIcon} />
                  <Typography variant='subtitle1' className={classes.packTypeTitle}>
                    Pack regular
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Muestras exactamente qué producto recibirá el usuario.
                  </Typography>
                </CardActionArea>
              </Paper>
            </Grid>
            {surprisePackHandlingEnabled && (
              <Grid item xs={12} sm={6}>
                <Paper className={classes.packTypeCard} elevation={0}>
                  <CardActionArea onClick={() => selectPackType(true)} className={classes.packTypeCardContent}>
                    <AutoAwesome className={`${classes.packTypeIcon} ${classes.surpriseIcon}`} />
                    <Typography variant='subtitle1' className={classes.packTypeTitle}>
                      Pack {SURPRISE_NAME_PREFIX_LABEL}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      El contenido puede variar; describe opciones claras sin prometer productos exactos.
                    </Typography>
                  </CardActionArea>
                </Paper>
              </Grid>
            )}
          </Grid>
        </DialogContent>
      </Dialog>

      <PackDialog
        isOpen={createDialogIsOpen}
        onClose={() => closeCreateDialog(false)}
        onSuccess={() => closeCreateDialog(true)}
        initialIsSurprise={createIsSurprise}
        callback={(pack: PackBeingCreated) => api.packs.create(pack, store.id)}
      />
      {loading ? (
        <Spinner />
      ) : (
        <>
          <RefreshIndicator visible={refreshing} />
          <Toast
            open={showToast}
            duration={5000}
            onClose={() => setShowToast(false)}
            severity='info'
            message='La cantidad disponible del pack se actualizó automáticamente en base a los pedidos.'
          />
          {packs?.length === 0 ? (
            <EmptyState title='¡Aún no tienes packs!' infoLines={['Publica tu primer pack para que sea mostrado en la app.']} />
          ) : (
            <Grid container spacing={2}>
              {packs.map((pack) => (
                <Grid item key={pack.id} xs={12} sm={6} md={3} lg={2}>
                  <PackCard
                    pack={pack}
                    refreshPacks={refreshPacksSameParams}
                    packIdBeingEdited={packIdBeingEdited}
                    setPackIdBeingEdited={setPackIdBeingEdited}
                    key={pack.id}
                  />
                </Grid>
              ))}
            </Grid>
          )}
          <Box className={classes.paginationContainer}>
            <Pagination
              count={numPages}
              page={page}
              color='primary'
              size={mobile ? 'small' : 'large'}
              siblingCount={0}
              boundaryCount={1}
              onChange={handlePagination}
            />
          </Box>
        </>
      )}
    </>
  );
}
